import React, { useState, useEffect } from 'react';
import { getCarouselSettings, saveCarouselSettings, getCarouselSlides } from '../services/apiService';
import { useToast } from '../hooks/useToast';
import Toast from '../components/Toast';
import { Settings, Save, ChevronLeft, ChevronRight, Play, Pause, MonitorPlay } from 'lucide-react';

const transitionTypes = [
  { id: 'fade', name: 'Fade', description: 'Smooth fade in/out', icon: '✨' },
  { id: 'slide', name: 'Slide', description: 'Horizontal slide', icon: '↔️' },
  { id: 'zoom', name: 'Zoom', description: 'Zoom in/out', icon: '🔍' },
  { id: 'rotate', name: 'Rotate', description: 'Rotation effect', icon: '🔄' },
];

const easingFunctions = [
  { id: 'linear', name: 'Linear', description: 'Constant speed' },
  { id: 'ease', name: 'Ease', description: 'Slow start/end' },
  { id: 'ease-in', name: 'Ease In', description: 'Slow start' },
  { id: 'ease-out', name: 'Ease Out', description: 'Slow end' },
  { id: 'ease-in-out', name: 'Ease In Out', description: 'Smooth start/end' },
];

const AdminCarouselSettings = () => {
  const [settings, setSettings] = useState({
    transition_type: 'fade',
    transition_duration: 1200,
    auto_play_interval: 4500,
    auto_play_enabled: true,
    easing_function: 'ease-in-out',
    pause_on_hover: true,
    show_navigation: false,
    show_indicators: true,
  });
  const [slides, setSlides] = useState([]);
  const [previewSlide, setPreviewSlide] = useState(0);
  const [previewInterval, setPreviewInterval] = useState(null);
  const [isHovering, setIsHovering] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { toast, showToast, hideToast } = useToast();

  useEffect(() => {
    loadSettings();
    loadSlides();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!loading && slides.length > 1 && settings.auto_play_enabled) {
      startPreview();
      return () => {
        if (previewInterval) clearInterval(previewInterval);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, slides.length, settings.auto_play_enabled]);

  const loadSettings = async () => {
    try {
      const data = await getCarouselSettings();
      setSettings({
        transition_type: data.transition_type || 'fade',
        transition_duration: data.transition_duration ?? 1200,
        auto_play_interval: data.auto_play_interval ?? 4500,
        auto_play_enabled: data.auto_play_enabled ?? true,
        easing_function: data.easing_function || 'ease-in-out',
        pause_on_hover: data.pause_on_hover ?? true,
        show_navigation: data.show_navigation ?? false,
        show_indicators: data.show_indicators ?? true,
      });
    } catch (error) {
      console.error('Error loading settings:', error);
      showToast('Failed to load settings', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadSlides = async () => {
    try {
      const data = await getCarouselSlides();
      const validSlides = data.filter(slide => {
        const url = slide.image_url || slide.url;
        return url && !url.startsWith('blob:');
      });
      setSlides(validSlides.slice(0, 3));
    } catch (error) {
      console.error('Error loading slides:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSettings({
      ...settings,
      [name]: type === 'checkbox' ? checked : type === 'number' ? parseInt(value) : value,
    });
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await saveCarouselSettings(settings);
      showToast('Settings saved successfully!', 'success');
      window.dispatchEvent(new CustomEvent('dataUpdated'));
      window.dispatchEvent(new CustomEvent('carouselSettingsUpdated', { detail: settings }));
    } catch (error) {
      console.error('Error saving settings:', error);
      showToast('Failed to save settings', 'error');
    } finally {
      setSaving(false);
    }
  };

  const startPreview = () => {
    if (slides.length < 2) return;
    if (previewInterval) clearInterval(previewInterval);
    const interval = setInterval(() => {
      if (!isHovering || !settings.pause_on_hover) {
        setPreviewSlide((prev) => (prev + 1) % slides.length);
      }
    }, settings.auto_play_interval);
    setPreviewInterval(interval);
  };

  // const goToSlide = (index) => setPreviewSlide(index); // Reserved for future use
  const nextSlide = () => setPreviewSlide((prev) => (prev + 1) % slides.length);
  const prevSlide = () => setPreviewSlide((prev) => (prev - 1 + slides.length) % slides.length);

  // Auto-restart preview logic
  useEffect(() => {
    if (!loading && slides.length > 1 && settings.auto_play_enabled) {
      if (previewInterval) clearInterval(previewInterval);
      const interval = setInterval(() => {
        if (!isHovering || !settings.pause_on_hover) {
          setPreviewSlide((prev) => (prev + 1) % slides.length);
        }
      }, settings.auto_play_interval);
      setPreviewInterval(interval);
      return () => clearInterval(interval);
    } else if (!settings.auto_play_enabled && previewInterval) {
      clearInterval(previewInterval);
      setPreviewInterval(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings.auto_play_interval, settings.pause_on_hover, settings.auto_play_enabled, loading, slides.length]);

  const getTransitionStyle = (index) => {
    const isActive = index === previewSlide;
    const duration = (settings.transition_duration || 1200) / 1000;
    const transitionType = settings.transition_type || 'fade';
    const easing = settings.easing_function || 'ease-in-out';

    const baseStyle = {
      position: 'absolute',
      inset: 0,
      transition: `all ${duration}s ${easing}`,
      zIndex: isActive ? 10 : 1,
    };

    switch (transitionType) {
      case 'fade': return { ...baseStyle, opacity: isActive ? 1 : 0 };
      case 'slide': return { ...baseStyle, opacity: isActive ? 1 : 0, transform: isActive ? 'translateX(0)' : `translateX(${index < previewSlide ? '-100%' : '100%'})` };
      case 'zoom': return { ...baseStyle, opacity: isActive ? 1 : 0, transform: isActive ? 'scale(1)' : 'scale(0.8)' };
      case 'rotate': return { ...baseStyle, opacity: isActive ? 1 : 0, transform: isActive ? 'rotate(0deg) scale(1)' : 'rotate(5deg) scale(0.9)' };
      default: return { ...baseStyle, opacity: isActive ? 1 : 0 };
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 md:space-y-8 pb-20 md:pb-0">
      <Toast toast={toast} hideToast={hideToast} />
      
      {/* Header */}
      <div>
        <h1 className="text-xl md:text-2xl font-bold text-slate-900 flex items-center gap-2">
          <Settings className="text-red-600 w-6 h-6 md:w-7 md:h-7" />
          Carousel Settings
        </h1>
        <p className="text-slate-500 text-xs md:text-sm mt-1">Configure transition effects and animations.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
        
        {/* PREVIEW PANEL (Sticky on Desktop) */}
        <div className="lg:sticky lg:top-24 order-1 lg:order-2">
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                <div className="p-4 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
                    <div className="flex items-center gap-2">
                        <MonitorPlay size={18} className="text-slate-400" />
                        <h2 className="font-bold text-slate-700 text-sm">Live Preview</h2>
                    </div>
                    {settings.auto_play_enabled ? (
                        <span className="text-xs font-bold text-green-600 bg-green-50 px-2 py-1 rounded flex items-center gap-1">
                            <Play size={10} fill="currentColor" /> Auto-playing
                        </span>
                    ) : (
                         <span className="text-xs font-bold text-slate-500 bg-slate-100 px-2 py-1 rounded flex items-center gap-1">
                            <Pause size={10} fill="currentColor" /> Paused
                        </span>
                    )}
                </div>

                <div className="p-4 md:p-6">
                    {slides.length === 0 ? (
                        <div className="aspect-[16/9] md:aspect-video rounded-lg bg-slate-100 flex items-center justify-center border-2 border-dashed border-slate-200">
                            <p className="text-slate-400 text-sm">No slides available</p>
                        </div>
                    ) : (
                        <div 
                            className="relative aspect-[16/9] md:aspect-video rounded-lg overflow-hidden bg-slate-900 shadow-inner group"
                            onMouseEnter={() => setIsHovering(true)}
                            onMouseLeave={() => setIsHovering(false)}
                        >
                            {slides.map((slide, index) => {
                                const imageUrl = slide.image_url || slide.url;
                                if (!imageUrl) return null;
                                return (
                                    <div key={`prev-${index}`} style={getTransitionStyle(index)}>
                                        <img src={imageUrl} alt="" className="w-full h-full object-cover" />
                                        {/* Overlay Card Preview */}
                                        {slide.show_card && index === previewSlide && (
                                            <div className="absolute bottom-4 left-4 right-4 md:right-auto md:max-w-xs bg-white/90 backdrop-blur p-3 rounded-lg shadow-lg text-left animate-in fade-in slide-in-from-bottom-2">
                                                <p className="font-bold text-slate-900 text-sm truncate">{slide.card_title || 'Title'}</p>
                                                <p className="text-xs text-slate-600 line-clamp-2 mt-0.5">{slide.card_description || 'Description text goes here...'}</p>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                            
                            {/* Controls */}
                            {settings.show_navigation && (
                                <>
                                    <button onClick={(e) => {e.stopPropagation(); prevSlide()}} className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/30 hover:bg-black/50 text-white p-1.5 rounded-full backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-opacity">
                                        <ChevronLeft size={20} />
                                    </button>
                                    <button onClick={(e) => {e.stopPropagation(); nextSlide()}} className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/30 hover:bg-black/50 text-white p-1.5 rounded-full backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-opacity">
                                        <ChevronRight size={20} />
                                    </button>
                                </>
                            )}
                            {settings.show_indicators && (
                                <div className="absolute bottom-3 left-0 right-0 flex justify-center gap-1.5">
                                    {slides.map((_, i) => (
                                        <div key={i} className={`h-1.5 rounded-full transition-all ${i === previewSlide ? 'w-4 bg-white' : 'w-1.5 bg-white/50'}`} />
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>

        {/* SETTINGS PANEL */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 md:p-6 space-y-6 order-2 lg:order-1">
          
          {/* Transition Type */}
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-3">Transition Effect</label>
            <div className="grid grid-cols-2 gap-3">
              {transitionTypes.map((type) => (
                <button
                  key={type.id}
                  onClick={() => setSettings({ ...settings, transition_type: type.id })}
                  className={`p-3 rounded-lg border text-left transition-all ${
                    settings.transition_type === type.id
                      ? 'border-red-600 bg-red-50 ring-1 ring-red-600'
                      : 'border-slate-200 hover:border-slate-300 bg-white'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-base">{type.icon}</span>
                    <span className="font-bold text-slate-900 text-sm">{type.name}</span>
                  </div>
                  <div className="text-[10px] text-slate-500 leading-tight">{type.description}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Easing */}
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">Easing Function</label>
            <select
              name="easing_function"
              value={settings.easing_function}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:border-red-500 focus:ring-1 focus:ring-red-500 bg-slate-50"
            >
              {easingFunctions.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
            </select>
          </div>

          {/* Sliders */}
          <div className="space-y-6">
            <div>
                <div className="flex justify-between mb-2">
                    <label className="text-xs font-bold text-slate-500 uppercase tracking-wide">Transition Speed</label>
                    <span className="text-xs font-mono font-bold text-red-600 bg-red-50 px-1.5 py-0.5 rounded">{settings.transition_duration}ms</span>
                </div>
                <input
                    type="range"
                    name="transition_duration"
                    min="300" max="3000" step="100"
                    value={settings.transition_duration}
                    onChange={handleChange}
                    className="w-full h-2 bg-slate-100 rounded-lg appearance-none cursor-pointer accent-red-600"
                />
            </div>

            <div>
                <div className="flex justify-between mb-2">
                    <label className="text-xs font-bold text-slate-500 uppercase tracking-wide">Auto-Play Interval</label>
                    <span className="text-xs font-mono font-bold text-red-600 bg-red-50 px-1.5 py-0.5 rounded">{(settings.auto_play_interval / 1000).toFixed(1)}s</span>
                </div>
                <input
                    type="range"
                    name="auto_play_interval"
                    min="2000" max="15000" step="500"
                    value={settings.auto_play_interval}
                    onChange={handleChange}
                    className="w-full h-2 bg-slate-100 rounded-lg appearance-none cursor-pointer accent-red-600"
                />
            </div>
          </div>

          {/* Toggles */}
          <div className="space-y-3 pt-2 border-t border-slate-100">
            {[
                { key: 'auto_play_enabled', label: 'Enable Auto Play' },
                { key: 'pause_on_hover', label: 'Pause on Hover' },
                { key: 'show_navigation', label: 'Show Arrows' },
                { key: 'show_indicators', label: 'Show Dots' }
            ].map((toggle) => (
                <label key={toggle.key} className="flex items-center justify-between cursor-pointer p-2 hover:bg-slate-50 rounded-lg transition-colors">
                    <span className="text-sm font-medium text-slate-700">{toggle.label}</span>
                    <div className="relative inline-flex items-center cursor-pointer">
                        <input type="checkbox" name={toggle.key} checked={settings[toggle.key]} onChange={handleChange} className="sr-only peer" />
                        <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-red-600"></div>
                    </div>
                </label>
            ))}
          </div>

          {/* Action Button */}
          <div className="pt-4">
            <button
                onClick={handleSave}
                disabled={saving}
                className="w-full bg-slate-900 text-white py-3 rounded-xl font-bold hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2 shadow-lg shadow-slate-900/10"
            >
                {saving ? <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Save size={18} />}
                {saving ? 'Saving...' : 'Save Configuration'}
            </button>
          </div>

        </div>

      </div>
    </div>
  );
};

export default AdminCarouselSettings;